import React, { useState, useEffect } from 'react';
import { FormControlLabel, Switch, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  InputAdornment
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CalculateIcon from '@mui/icons-material/Calculate';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';

// 格式化数字为千分位
const formatNumber = (num) => {
  if (num === undefined || num === null) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 金字塔仓位计算器组件
const PyramidCalculator = ({ showIntroFromParent, tradingSettings = { market: 'A股', commissionRate: 0.03, stampDutyRate: 0.1, platformFee: 0, minFee: 0 } }) => {
  const theme = useTheme();
  
  // 通用手续费计算函数
  const calculateTradingFees = (amount, shares, isBuy = true) => {
    const market = tradingSettings.market || 'A股';
    let commission = 0;
    let stampDuty = 0;
    let totalFee = 0;
    
    if (market === 'A股') {
      // A股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = isBuy ? 0 : amount * (tradingSettings.stampDutyRate / 100); // 买入不收印花税
      totalFee = commission + stampDuty;
    } else if (market === '港股') {
      // 港股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = amount * (tradingSettings.stampDutyRate / 100); // 买卖都收印花税
      totalFee = commission + stampDuty;
    } else if (market === '美股') {
      // 美股费率计算
      const perShareFee = shares * tradingSettings.platformFee;
      totalFee = Math.max(perShareFee, tradingSettings.minFee);
      commission = totalFee; // 美股没有单独的佣金和印花税概念
      stampDuty = 0;
    }
    
    return {
      commission: commission,
      stampDuty: stampDuty,
      totalFee: totalFee
    };
  };
  
  // 状态定义
  const [initialCapital, setInitialCapital] = useState(100000); // 初始资金，默认10万
  const [targetPrice, setTargetPrice] = useState(20); // 目标价格
  const [initialPrice, setInitialPrice] = useState(10); // 初始价格
  const [decreaseRate, setDecreaseRate] = useState(10); // 每级上涨/下跌百分比
  const [results, setResults] = useState([]); // 计算结果
  const [summary, setSummary] = useState(null); // 汇总信息
  const [showIntro, setShowIntro] = useState(false); // 控制介绍显示
  const [direction, setDirection] = useState('buy'); // 方向：建仓(buy)或清仓(sell)
  const [sharesPerLot, setSharesPerLot] = useState(100); // 每手股数，默认100股
  const [totalShares, setTotalShares] = useState(10000); // 清仓模式下的持有总股数，默认10000股
  const [currentPrice, setCurrentPrice] = useState(15); // 清仓模式下的当前价格，默认15
  // 金字塔比例设置
  const [levelRatios, setLevelRatios] = useState([40, 30, 20, 10]); // 默认4级比例
  
  // 当父组件传入showIntroFromParent变化时，更新本地的showIntro状态
  useEffect(() => {
    if (showIntroFromParent) {
      setShowIntro(true);
    }
  }, [showIntroFromParent]);

  // 验证输入是否为有效数字
  const isValidNumber = (value) => {
    return !isNaN(value) && value > 0;
  };

  // 重置表单
  const handleReset = () => {
    setInitialCapital(100000);
    setTargetPrice(20);
    setInitialPrice(10);
    setDecreaseRate(10);
    setDirection('buy');
    setSharesPerLot(100);
    setTotalShares(0);
    setCurrentPrice(0);
    setLevelRatios([40, 30, 20, 10]);
    setResults([]);
    setSummary(null);
  };

  // 计算金字塔加仓/减仓策略
  const calculatePyramid = () => {
    // 验证输入
    if (!isValidNumber(targetPrice) || 
        !isValidNumber(decreaseRate) ||
        !isValidNumber(sharesPerLot) || !isValidNumber(tradingSettings.commissionRate) ||
        !isValidNumber(tradingSettings.stampDutyRate)) {
      alert('请输入有效的参数值！');
      return;
    }

    // 验证比例总和是否为100%
    const totalRatio = levelRatios.reduce((sum, ratio) => sum + ratio, 0);
    if (Math.abs(totalRatio - 100) > 0.01) {
      alert('金字塔比例总和必须为100%！');
      return;
    }

    // 建仓模式额外验证
    if (direction === 'buy' && (!isValidNumber(initialCapital) || !isValidNumber(initialPrice))) {
      alert('建仓模式下，请输入有效的初始资金和初始价格！');
      return;
    }

    // 清仓模式额外验证
    if (direction === 'sell' && (!isValidNumber(totalShares) || !isValidNumber(initialPrice) || !isValidNumber(currentPrice))) {
      alert('清仓模式下，请输入有效的持有股数、持有价格和当前价格！');
      return;
    }

    const pyramidResults = [];
    let totalSharesCount = 0;
    let totalInvestment = 0;
    let totalCommission = 0;
    let totalStampDuty = 0;
    let totalFees = 0;
    let currentPriceValue = direction === 'buy' ? initialPrice : currentPrice;
    let remainingShares = direction === 'sell' ? totalShares : 0;
    const levels = levelRatios.length;

    // 建仓模式计算
    if (direction === 'buy') {
      // 计算每一级的加仓
      for (let i = 0; i < levels; i++) {
        // 计算当前级别的价格（价格上涨）
        if (i === 0) {
          // 第一级使用初始价格
          currentPriceValue = initialPrice;
        } else {
          // 后续级别价格上涨
          currentPriceValue = initialPrice * Math.pow(1 + decreaseRate / 100, i);
        }
        
        // 计算当前级别的资金比例
        const levelRatio = levelRatios[i] / 100;
        const currentCapitalValue = initialCapital * levelRatio;
        
        // 计算股数，确保是每手股数的整数倍
        const lotCount = Math.floor(currentCapitalValue / (currentPriceValue * sharesPerLot));
        const shares = lotCount * sharesPerLot;
        
        // 计算手续费
        const investment = shares * currentPriceValue;
        const fees = calculateTradingFees(investment, shares, true);
        const commission = fees.commission;
        const stampDuty = fees.stampDuty;
        const totalCost = investment + fees.totalFee;
        
        totalSharesCount += shares;
        totalInvestment += totalCost;
        totalCommission += commission;
        totalStampDuty += stampDuty;
        totalFees += fees.totalFee;

        // 计算预期收益率
        const expectedProfit = shares * (targetPrice - currentPriceValue);
        // 防止除以零错误
        const expectedProfitRate = totalCost > 0 ? (expectedProfit / totalCost) * 100 : 0;

        pyramidResults.push({
          level: i + 1,
          price: currentPriceValue.toFixed(2),
          capital: currentCapitalValue.toFixed(2),
          shares: shares,
          commission: commission.toFixed(2),
          stampDuty: stampDuty.toFixed(2),
          investment: totalCost.toFixed(2),
          totalShares: totalSharesCount,
          totalInvestment: totalInvestment.toFixed(2),
          averagePrice: (totalInvestment / totalSharesCount).toFixed(2),
          expectedProfit: expectedProfit.toFixed(2),
          expectedProfitRate: expectedProfitRate.toFixed(2),
          // 添加格式化后的值用于显示
          formattedPrice: formatNumber(currentPriceValue.toFixed(2)),
          formattedCapital: formatNumber(currentCapitalValue.toFixed(2)),
          formattedShares: formatNumber(shares),
          formattedCommission: formatNumber(commission.toFixed(2)),
          formattedStampDuty: formatNumber(stampDuty.toFixed(2)),
          formattedInvestment: formatNumber(totalCost.toFixed(2)),
          formattedTotalShares: formatNumber(totalSharesCount),
          formattedTotalInvestment: formatNumber(totalInvestment.toFixed(2)),
          formattedAveragePrice: formatNumber((totalInvestment / totalSharesCount).toFixed(2)),
          formattedExpectedProfit: formatNumber(expectedProfit.toFixed(2)),
          formattedExpectedProfitRate: isNaN(expectedProfitRate) ? '0.00' : formatNumber(expectedProfitRate.toFixed(2))
        });
      }

      // 计算汇总信息
      const averagePrice = totalInvestment / totalSharesCount;
      const profitAtTarget = totalSharesCount * (targetPrice - averagePrice);
      const profitPercentage = ((targetPrice / averagePrice) - 1) * 100;

      setSummary({
        totalInvestment: totalInvestment.toFixed(2),
        totalShares: totalSharesCount,
        averagePrice: averagePrice.toFixed(2),
        profitAtTarget: profitAtTarget.toFixed(2),
        profitPercentage: profitPercentage.toFixed(2),
        totalCommission: totalCommission.toFixed(2),
        totalStampDuty: totalStampDuty.toFixed(2),
        totalFees: totalFees.toFixed(2),
        // 添加格式化后的值用于显示
        formattedTotalInvestment: formatNumber(totalInvestment.toFixed(2)),
        formattedTotalShares: formatNumber(totalSharesCount),
        formattedAveragePrice: formatNumber(averagePrice.toFixed(2)),
        formattedProfitAtTarget: formatNumber(profitAtTarget.toFixed(2)),
        formattedProfitPercentage: formatNumber(profitPercentage.toFixed(2)),
        formattedTotalCommission: formatNumber(totalCommission.toFixed(2)),
        formattedTotalStampDuty: formatNumber(totalStampDuty.toFixed(2)),
        formattedTotalFees: formatNumber(totalFees.toFixed(2))
      });
    } else {
      // 清仓模式计算
      let totalSellValue = 0;
      let totalSoldShares = 0;
      let remainingSharesCount = totalShares;
      let currentPriceValue = 0;
      totalCommission = 0;
      totalStampDuty = 0;
      totalFees = 0;
      
      // 计算持仓成本
      const holdingCost = totalShares * initialPrice;
      
      // 计算每一级的减仓
      for (let i = 0; i < levels && remainingSharesCount > 0; i++) {
        // 计算当前级别的价格（价格上涨）
        if (i === 0) {
          // 第一级使用当前价格
          currentPriceValue = currentPrice;
        } else {
          // 后续级别价格上涨
          currentPriceValue = currentPrice * Math.pow(1 + decreaseRate / 100, i);
        }
        
        // 计算当前级别的卖出比例
        const levelRatio = levelRatios[i] / 100;
        
        // 计算卖出股数，确保是每手股数的整数倍
        const sharesToSell = Math.min(
          Math.floor((totalShares * levelRatio) / sharesPerLot) * sharesPerLot,
          remainingSharesCount
        );
        
        // 如果没有剩余股票可卖，则跳出循环
        if (sharesToSell <= 0) break;
        
        // 计算手续费
        const sellValue = sharesToSell * currentPriceValue;
        const fees = calculateTradingFees(sellValue, sharesToSell, false);
        const commission = fees.commission;
        const stampDuty = fees.stampDuty;
        const netSellValue = sellValue - fees.totalFee;
        
        // 计算该级别的利润和利润率
        const levelCost = (sharesToSell / totalShares) * holdingCost;
        const levelProfit = netSellValue - levelCost;
        // 防止除以零错误
        const levelProfitRate = levelCost > 0 ? (levelProfit / levelCost) * 100 : 0;
        
        totalSellValue += netSellValue;
        totalCommission += commission;
        totalStampDuty += stampDuty;
        totalFees += fees.totalFee;
        totalSoldShares += sharesToSell;
        remainingSharesCount -= sharesToSell;

        pyramidResults.push({
          level: i + 1,
          price: currentPriceValue.toFixed(2),
          sharesToSell: sharesToSell,
          commission: commission.toFixed(2),
          stampDuty: stampDuty.toFixed(2),
          sellValue: netSellValue.toFixed(2),
          totalSoldShares: totalSoldShares,
          remainingShares: remainingSharesCount,
          totalSellValue: totalSellValue.toFixed(2),
          levelProfit: levelProfit.toFixed(2),
          levelProfitRate: levelProfitRate.toFixed(2),
          // 添加格式化后的值用于显示
          formattedPrice: formatNumber(currentPriceValue.toFixed(2)),
          formattedSharesToSell: formatNumber(sharesToSell),
          formattedCommission: formatNumber(commission.toFixed(2)),
          formattedStampDuty: formatNumber(stampDuty.toFixed(2)),
          formattedSellValue: formatNumber(netSellValue.toFixed(2)),
          formattedTotalSoldShares: formatNumber(totalSoldShares),
          formattedRemainingShares: formatNumber(remainingSharesCount),
          formattedTotalSellValue: formatNumber(totalSellValue.toFixed(2)),
          formattedLevelProfit: formatNumber(levelProfit.toFixed(2)),
          formattedLevelProfitRate: isNaN(levelProfitRate) ? '0.00%' : formatNumber(levelProfitRate.toFixed(2)) + '%'
        });
      }
      
      // 计算汇总信息
      const averageSellPrice = totalSoldShares > 0 ? totalSellValue / totalSoldShares : 0;
      const profit = totalSellValue - (totalSoldShares * initialPrice);
      const profitPercentage = totalSoldShares > 0 ? (profit / (totalSoldShares * initialPrice)) * 100 : 0;
      
      setSummary({
        totalSellValue: totalSellValue.toFixed(2),
        totalSoldShares: totalSoldShares,
        remainingShares: remainingSharesCount,
        averageSellPrice: averageSellPrice.toFixed(2),
        profit: profit.toFixed(2),
        profitPercentage: profitPercentage.toFixed(2),
        totalCommission: totalCommission.toFixed(2),
        totalStampDuty: totalStampDuty.toFixed(2),
        totalFees: totalFees.toFixed(2),
        // 添加格式化后的值用于显示
        formattedTotalSellValue: formatNumber(totalSellValue.toFixed(2)),
        formattedTotalSoldShares: formatNumber(totalSoldShares),
        formattedRemainingShares: formatNumber(remainingSharesCount),
        formattedAverageSellPrice: formatNumber(averageSellPrice.toFixed(2)),
        formattedProfit: formatNumber(profit.toFixed(2)),
        formattedProfitPercentage: formatNumber(profitPercentage.toFixed(2)),
        formattedTotalCommission: formatNumber(totalCommission.toFixed(2)),
        formattedTotalStampDuty: formatNumber(totalStampDuty.toFixed(2)),
        formattedTotalFees: formatNumber(totalFees.toFixed(2))
      });
    }

    setResults(pyramidResults);
  };

  // 切换介绍显示
  const toggleIntro = () => {
    setShowIntro(!showIntro);
    
    // 如果显示介绍，则滚动到介绍部分
    if (!showIntro) {
      setTimeout(() => {
        const element = document.getElementById('pyramid-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* 计算器表单 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalculateIcon sx={{ mr: 1 }} />
              金字塔仓位计算器
              <Tooltip title="查看金字塔买卖法介绍">
                <IconButton size="small" onClick={toggleIntro} sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>操作方向</InputLabel>
                  <Select
                    value={direction}
                    label="操作方向"
                    onChange={(e) => setDirection(e.target.value)}
                  >
                    <MenuItem value="buy">建仓（价格上涨加仓）</MenuItem>
                    <MenuItem value="sell">清仓（价格上涨减仓）</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="每手股数"
                  type="number"
                  fullWidth
                  value={sharesPerLot}
                  onChange={(e) => {
                    let value = e.target.value;
                    // 如果用户输入了值，则直接使用该值，不进行Number转换
                    // 这样可以保留用户输入的所有数字，包括末尾的0
                    setSharesPerLot(value === '' ? '' : value);
                  }}
                  inputProps={{ min: 1 }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              {/* 建仓模式特有字段 */}
              {direction === 'buy' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="预计总动用资金"
                      type="number"
                      fullWidth
                      value={initialCapital}
                      onChange={(e) => {
                        let value = e.target.value;
                        // 如果用户输入了值，则直接使用该值，不进行Number转换
                        // 这样可以保留用户输入的所有数字，包括末尾的0
                        setInitialCapital(value === '' ? '' : value);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="开始价格"
                      type="number"
                      fullWidth
                      value={initialPrice}
                      onChange={(e) => {
                        let value = e.target.value;
                        // 如果用户输入了值，则直接使用该值，不进行Number转换
                        // 这样可以保留用户输入的所有数字，包括末尾的0
                        setInitialPrice(value === '' ? '' : value);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {/* 清仓模式特有字段 */}
              {direction === 'sell' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="持有股数"
                      type="number"
                      fullWidth
                      value={totalShares}
                      onChange={(e) => {
                        let value = e.target.value;
                        // 如果用户输入了值，则直接使用该值，不进行Number转换
                        // 这样可以保留用户输入的所有数字，包括末尾的0
                        setTotalShares(value === '' ? '' : value);
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="持有价格"
                      type="number"
                      fullWidth
                      value={initialPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        // 移除前导零
                        const cleanedValue = value.replace(/^0+(?=\d)/, '');
                        setInitialPrice(Number(cleanedValue));
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="开始清仓价格"
                      type="number"
                      fullWidth
                      value={currentPrice}
                      onChange={(e) => {
                        let value = e.target.value;
                        // 如果用户输入了值，则直接使用该值，不进行Number转换
                        // 这样可以保留用户输入的所有数字，包括末尾的0
                        setCurrentPrice(value === '' ? '' : value);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {direction === 'buy' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="目标价格"
                    type="number"
                    fullWidth
                    value={targetPrice}
                    onChange={(e) => {
                      let value = e.target.value;
                      // 如果用户输入了值，则直接使用该值，不进行Number转换
                      // 这样可以保留用户输入的所有数字，包括末尾的0
                      setTargetPrice(value === '' ? '' : value);
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={direction === 'buy' ? "每级上涨百分比触发" : "每级上涨百分比触发"}
                  type="number"
                  fullWidth
                  value={decreaseRate}
                  onChange={(e) => {
                    let value = e.target.value;
                    // 如果用户输入了值，则直接使用该值，不进行Number转换
                    // 这样可以保留用户输入的所有数字，包括末尾的0
                    setDecreaseRate(value === '' ? '' : value);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography variant="subtitle2" gutterBottom>
                  仓位比例设置 (总和必须为100%)
                </Typography>
                <Grid container spacing={1}>
                  {levelRatios.map((ratio, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <TextField
                        label={`第${index + 1}级比例`}
                        type="number"
                        fullWidth
                        value={ratio}
                        onChange={(e) => {
                          let value = e.target.value;
                          // 如果用户输入了值，则直接使用该值，不进行Number转换
                          // 这样可以保留用户输入的所有数字，包括末尾的0
                          const newRatios = [...levelRatios];
                          newRatios[index] = value === '' ? '' : value;
                          setLevelRatios(newRatios);
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={calculatePyramid}
                    startIcon={<CalculateIcon />}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    计算
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltIcon />}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    重置
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 计算结果和汇总 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              计算结果
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {summary && (
              <Card sx={{ mb: 3, bgcolor: theme.palette.primary.light + '15' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    汇总信息
                  </Typography>
                  <Grid container spacing={1}>
                    {direction === 'buy' ? (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">总投资金额: ¥{summary.formattedTotalInvestment}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总持仓数量: {summary.formattedTotalShares}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">平均成本价: ¥{summary.formattedAveragePrice}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color={Number(summary.profitAtTarget) >= 0 ? 'success.main' : 'error.main'}>
                            目标价盈亏: ¥{summary.formattedProfitAtTarget}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color={Number(summary.profitPercentage) >= 0 ? 'success.main' : 'error.main'}>
                            目标价收益率: {summary.formattedProfitPercentage}%
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">总卖出金额: ¥{summary.formattedTotalSellValue}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总卖出数量: {summary.formattedTotalSoldShares}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">平均卖出价: ¥{summary.formattedAverageSellPrice}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">剩余持仓: {summary.formattedRemainingShares}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color={Number(summary.profit) >= 0 ? 'success.main' : 'error.main'}>
                            盈亏: ¥{summary.formattedProfit}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color={Number(summary.profitPercentage) >= 0 ? 'success.main' : 'error.main'}>
                            收益率: {summary.formattedProfitPercentage}%
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
            
            {results.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>层级</TableCell>
                      {direction === 'buy' ? (
                        <>
                          <TableCell>价格</TableCell>
                          <TableCell>投入资金</TableCell>
                          <TableCell>买入数量</TableCell>
    
                          <TableCell>累计成本</TableCell>
                          <TableCell>平均成本</TableCell>
                          <TableCell>预期收益</TableCell>
                          <TableCell>收益率</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>价格</TableCell>
                          <TableCell>卖出数量</TableCell>
    
                          <TableCell>卖出金额</TableCell>
                          <TableCell>累计卖出</TableCell>
                          <TableCell>剩余股数</TableCell>
                          <TableCell>级别收益</TableCell>
                          <TableCell>收益率</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row) => (
                      <TableRow key={row.level}>
                        <TableCell>{row.level}</TableCell>
                        {direction === 'buy' ? (
                          <>
                            <TableCell>¥{row.formattedPrice}</TableCell>
                            <TableCell>¥{row.formattedCapital}</TableCell>
                            <TableCell>{row.formattedShares}股</TableCell>
        
                            <TableCell>¥{row.formattedTotalInvestment}</TableCell>
                            <TableCell>¥{row.formattedAveragePrice}</TableCell>
                            <TableCell sx={{ color: Number(row.expectedProfit) >= 0 ? 'success.main' : 'error.main' }}>
                              ¥{row.formattedExpectedProfit}
                            </TableCell>
                            <TableCell sx={{ color: Number(row.expectedProfitRate) >= 0 ? 'success.main' : 'error.main' }}>
                              {row.formattedExpectedProfitRate}%
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>¥{row.formattedPrice}</TableCell>
                            <TableCell>{row.formattedSharesToSell}股</TableCell>
        
                            <TableCell>¥{row.formattedSellValue}</TableCell>
                            <TableCell>{row.formattedTotalSoldShares}股</TableCell>
                            <TableCell>{row.formattedRemainingShares}股</TableCell>
                            <TableCell sx={{ color: Number(row.levelProfit) >= 0 ? 'success.main' : 'error.main' }}>
                              ¥{row.formattedLevelProfit}
                            </TableCell>
                            <TableCell sx={{ color: Number(row.levelProfitRate) >= 0 ? 'success.main' : 'error.main' }}>
                              {row.formattedLevelProfitRate}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                请填写参数并点击计算按钮查看结果
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* 金字塔买卖法介绍 */}
        {showIntro && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }} id="pyramid-intro">
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 1 }} />
                金字塔买卖法介绍
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                一、金字塔仓位管理法（最经典）
              </Typography>
              <Typography variant="body2" paragraph>
                金字塔买卖法是一种分批交易的策略，既可用于建仓也可用于清仓。建仓时，核心思想是在价格上涨时逐步减少投资金额，形成类似金字塔形状的持仓结构；清仓时，同样是在价格上涨过程中逐步减少卖出数量，形成金字塔结构。这种方法特别适用于趋势明确的上涨行情中的建仓和清仓操作，能够有效地控制风险并最大化收益。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>原理：</strong>初始建仓资金比例较大，随着股价上涨，后续加仓比例逐步递减，形成"底部大、顶部小"的金字塔结构。初始进场的资金量比较大，后市如果行情按相反方向运行，则不再加仓，如果方向一致，逐步加仓，加仓比例越来越小。仓位控制呈下方大，上方小的形态，像一个金字塔，所以叫金字塔形的仓位管理方法。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>优点：</strong>有效控制平均成本，避免高位集中买入，风险较低。按照报酬率进行仓位控制，胜率越高动用的仓位就越高。利用趋势的持续性来增加仓位。在趋势中，会获得很高的收益，风险率较低。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>缺点：</strong>对趋势判断要求高，震荡市效果不佳。在震荡市中，较难获得收益。初始仓位较重，对于第一次入场的要求比较高。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>适用场景：</strong>趋势明确的牛市或上涨行情。
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                金字塔买卖法的精髓
              </Typography>
              <Typography variant="body2" component="div">
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                  建仓策略：
                </Typography>
                <ol>
                  <li>
                    <strong>顺势而为</strong>：在价格上涨趋势确立时，有计划地逐步买入，避免踏空。
                  </li>
                  <li>
                    <strong>资金管理</strong>：不会一次性投入全部资金，而是根据价格变化分批投入，有效控制风险。
                  </li>
                  <li>
                    <strong>心理优势</strong>：通过预设计划，减少情绪干扰，避免在市场上涨时因恐惧错过机会。
                  </li>
                  <li>
                    <strong>趋势跟踪</strong>：随着价格上涨确认趋势，逐步减少投入资金比例，降低高位买入风险。
                  </li>
                  <li>
                    <strong>适应市场趋势</strong>：特别适合趋势明确的上涨行情，能够在确认趋势后逐步建仓。
                  </li>
                </ol>
                
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                  清仓策略：
                </Typography>
                <ol>
                  <li>
                    <strong>顺势而为</strong>：在价格上涨时，有计划地逐步减少卖出数量，提高平均卖出价格。
                  </li>
                  <li>
                    <strong>仓位管理</strong>：不会一次性清空所有仓位，而是根据价格变化分批卖出，锁定部分利润的同时保留上涨空间。
                  </li>
                  <li>
                    <strong>情绪控制</strong>：避免因担心回调而过早全部卖出，也避免因贪婪而不愿卖出任何持仓。
                  </li>
                  <li>
                    <strong>收益最大化</strong>：通过在价格每上涨一定比例时减少卖出数量，提高整体收益率。
                  </li>
                  <li>
                    <strong>风险控制</strong>：即使后期价格回落，由于前期已经卖出较多仓位，整体风险可控。
                  </li>
                </ol>
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                金字塔仓位示意图
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                建仓金字塔
              </Typography>
              <Box sx={{ width: '100%', textAlign: 'center', my: 2 }}>
                <svg width="400" height="250" viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg">
                  {/* 背景 */}
                  <rect x="0" y="0" width="400" height="250" fill={theme.palette.background.paper} rx="5" ry="5" />
                  
                  {/* 金字塔形状 */}
                  <polygon points="200,30 50,220 350,220" fill={theme.palette.primary.light + '30'} stroke={theme.palette.primary.main} strokeWidth="2" />
                  
                  {/* 水平分层线 */}
                  <line x1="80" y1="190" x2="320" y2="190" stroke={theme.palette.primary.main} strokeWidth="1" strokeDasharray="5,5" />
                  <line x1="120" y1="150" x2="280" y2="150" stroke={theme.palette.primary.main} strokeWidth="1" strokeDasharray="5,5" />
                  <line x1="160" y1="110" x2="240" y2="110" stroke={theme.palette.primary.main} strokeWidth="1" strokeDasharray="5,5" />
                  
                  {/* 标签 */}
                  <text x="200" y="205" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第1级 (初始仓位)</text>
                  <text x="200" y="170" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第2级 (加仓1)</text>
                  <text x="200" y="130" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第3级 (加仓2)</text>
                  <text x="200" y="90" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第4级 (加仓3)</text>
                  
                  {/* 价格箭头 */}
                  <line x1="50" y1="220" x2="50" y2="30" stroke={theme.palette.success.main} strokeWidth="2" />
                  <polygon points="50,30 45,40 55,40" fill={theme.palette.success.main} />
                  <text x="30" y="125" textAnchor="middle" fill={theme.palette.success.main} fontSize="14" fontWeight="bold" transform="rotate(-90, 30, 125)">价格上涨</text>
                  
                  {/* 资金箭头 */}
                  <line x1="350" y1="220" x2="350" y2="30" stroke={theme.palette.primary.main} strokeWidth="2" />
                  <polygon points="350,30 345,40 355,40" fill={theme.palette.primary.main} />
                  <text x="370" y="125" textAnchor="middle" fill={theme.palette.primary.main} fontSize="14" fontWeight="bold" transform="rotate(90, 370, 125)">资金减少</text>
                  
                  {/* 说明文字 */}
                  <text x="200" y="240" textAnchor="middle" fill={theme.palette.text.secondary} fontSize="12">随着价格上涨，逐级减少投入资金，形成金字塔形状的仓位结构</text>
                </svg>
              </Box>
              <Typography variant="caption" display="block" textAlign="center" sx={{ mb: 2 }}>
                建仓金字塔示意：随着价格上涨，逐级减少投入资金，形成金字塔形状的仓位结构
              </Typography>
              
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                <strong>建仓金字塔使用要点：</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li>设置合理的初始资金和初始价格，通常建议初始仓位占比最大（如40%）</li>
                  <li>根据标的波动性设置合理的上涨比例，波动大的标的可设置5-10%，波动小的可设置2-5%</li>
                  <li>仓位比例设置为递减模式（如40%、30%、20%、10%），确保总和为100%</li>
                  <li>设置合理的目标价格，可以是技术分析得出的阻力位或基本面分析的合理估值</li>
                  <li><strong>重要：第一级操作完毕后，如果股价往相反的趋势走，不要再操作第二级，应立即启动止盈止损策略</strong></li>
                  <li>只有在股价按预期方向持续运行时，才考虑执行后续级别的操作</li>
                  <li>佣金和印花税率根据实际券商费率设置，以获得更准确的计算结果</li>
                </ul>
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                清仓金字塔
              </Typography>
              <Box sx={{ width: '100%', textAlign: 'center', my: 2 }}>
                <svg width="400" height="250" viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg">
                  {/* 背景 */}
                  <rect x="0" y="0" width="400" height="250" fill={theme.palette.background.paper} rx="5" ry="5" />
                  
                  {/* 金字塔形状 - 修改为4级结构 */}
                  <polygon points="200,30 50,220 350,220" fill={theme.palette.secondary.light + '30'} stroke={theme.palette.secondary.main} strokeWidth="2" />
                  
                  {/* 水平分层线 */}
                  <line x1="80" y1="190" x2="320" y2="190" stroke={theme.palette.secondary.main} strokeWidth="1" strokeDasharray="5,5" />
                  <line x1="120" y1="150" x2="280" y2="150" stroke={theme.palette.secondary.main} strokeWidth="1" strokeDasharray="5,5" />
                  <line x1="160" y1="110" x2="240" y2="110" stroke={theme.palette.secondary.main} strokeWidth="1" strokeDasharray="5,5" />
                  
                  {/* 标签 */}
                  <text x="200" y="205" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第1级 (初始卖出)</text>
                  <text x="200" y="170" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第2级 (减仓1)</text>
                  <text x="200" y="130" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第3级 (减仓2)</text>
                  <text x="200" y="90" textAnchor="middle" fill={theme.palette.text.primary} fontSize="12" fontWeight="bold">第4级 (减仓3)</text>
                  
                  {/* 价格箭头 */}
                  <line x1="50" y1="220" x2="50" y2="30" stroke={theme.palette.success.main} strokeWidth="2" />
                  <polygon points="50,30 45,40 55,40" fill={theme.palette.success.main} />
                  <text x="30" y="125" textAnchor="middle" fill={theme.palette.success.main} fontSize="14" fontWeight="bold" transform="rotate(-90, 30, 125)">价格上涨</text>
                  
                  {/* 数量箭头 */}
                  <line x1="350" y1="220" x2="350" y2="30" stroke={theme.palette.secondary.main} strokeWidth="2" />
                  <polygon points="350,30 345,40 355,40" fill={theme.palette.secondary.main} />
                  <text x="370" y="125" textAnchor="middle" fill={theme.palette.secondary.main} fontSize="14" fontWeight="bold" transform="rotate(90, 370, 125)">卖出数量减少</text>
                  
                  {/* 说明文字 */}
                  <text x="200" y="240" textAnchor="middle" fill={theme.palette.text.secondary} fontSize="12">随着价格上涨，逐级减少卖出数量，提高平均卖出价格</text>
                </svg>
              </Box>
              <Typography variant="caption" display="block" textAlign="center" sx={{ mb: 2 }}>
                清仓金字塔示意：随着价格上涨，逐级减少卖出数量，形成倒金字塔形状的减仓结构，提高平均卖出价格
              </Typography>
              
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                <strong>清仓金字塔使用要点：</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li>设置准确的总持仓数量和持仓均价，确保计算结果的准确性</li>
                  <li>根据标的波动性设置合理的上涨比例，波动大的标的可设置5-10%，波动小的可设置2-5%</li>
                  <li>卖出数量减少比例建议设置为递减模式（如40%、30%、20%、10%），确保总和为100%</li>
                  <li><strong>重要：第一级操作完毕后，如果股价往相反的趋势走，不要再操作第二级，应立即全部清仓止损</strong></li>
                  <li>只有在股价按预期方向持续运行时，才考虑执行后续级别的操作</li>
                  <li>当前价格应设置为您准备开始执行清仓计划时的市场价格</li>
                  <li>每个级别的卖出会实时计算收益和收益率，帮助您评估清仓进度和效果</li>
                </ul>
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                金字塔交易法的心理优势
              </Typography>
              <Typography variant="body2" paragraph>
                金字塔交易法不仅在数学上优化了交易结果，还能帮助克服交易中的常见心理障碍：
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li><strong>克服恐惧心理</strong>：预设分批建仓/清仓计划，减轻市场波动带来的恐慌情绪</li>
                  <li><strong>避免追涨杀跌</strong>：通过系统化的交易计划，避免情绪化决策</li>
                  <li><strong>减轻踏空遗憾</strong>：清仓时保留部分仓位，降低市场继续上涨时的遗憾感</li>
                  <li><strong>控制贪婪情绪</strong>：建仓时预留资金，清仓时分批获利，避免过度贪婪</li>
                  <li><strong>增强交易纪律</strong>：提前设定明确的交易计划，增强执行力和纪律性</li>
                  <li><strong>提高止损执行力</strong>：明确设定在趋势反转时立即止损的规则，避免因侥幸心理而持续亏损</li>
                </ul>
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                金字塔交易法与其他策略的比较
              </Typography>
              <Typography variant="body2" paragraph>
                相比其他常见的仓位管理策略，金字塔交易法有其独特优势：
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li><strong>vs 一次性建仓/清仓</strong>：降低时机选择风险，避免在最差时点全仓操作</li>
                  <li><strong>vs 等比例分批</strong>：通过调整每批资金比例，更有效地降低成本或提高收益</li>
                  <li><strong>vs 漏斗型策略</strong>：在趋势明确的行情中表现更佳，但要求严格执行趋势反转时的止损策略</li>
                  <li><strong>vs 矩形策略</strong>：在极端行情中表现更佳，能更有效地利用价格波动</li>
                  <li><strong>vs 马丁格尔策略</strong>：金字塔策略强调趋势跟踪和止损，而非马丁格尔的不断加码补仓，风险更可控</li>
                </ul>
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                使用建议
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                建仓策略使用建议
              </Typography>
              <Typography variant="body2" paragraph>
                1. <strong>严格执行止损</strong>：金字塔策略的核心是趋势跟踪，如果第一级操作后趋势反转，必须立即执行止损策略，不要继续加仓。设定明确的止损位，防止持续下跌带来的过大损失。
              </Typography>
              <Typography variant="body2" paragraph>
                2. <strong>选择标的</strong>：适用于基本面良好，但短期受市场情绪影响的优质标的，不适合基本面恶化的标的。
              </Typography>
              <Typography variant="body2" paragraph>
                3. <strong>资金规划</strong>：事先规划好每一级的资金投入，不要因情绪波动随意改变计划。
              </Typography>
              <Typography variant="body2" paragraph>
                4. <strong>耐心等待</strong>：金字塔策略是一种中长期策略，需要有足够的耐心等待市场回归理性。
              </Typography>
              <Typography variant="body2" paragraph>
                5. <strong>趋势确认</strong>：只有在趋势明确且持续的情况下才执行后续级别的操作，不要在震荡市中频繁操作。
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                清仓策略使用建议
              </Typography>
              <Typography variant="body2" paragraph>
                1. <strong>严格执行止损</strong>：如果第一级卖出后价格反向下跌，应立即全部清仓止损，不要继续执行后续级别的减仓计划。
              </Typography>
              <Typography variant="body2" paragraph>
                2. <strong>设定目标价</strong>：根据技术分析或基本面分析，设定合理的目标价格区间，避免无限期持有。
              </Typography>
              <Typography variant="body2" paragraph>
                3. <strong>关注转折信号</strong>：密切关注可能的市场转折信号，如成交量异常、技术指标背离等，适时调整清仓计划。
              </Typography>
              <Typography variant="body2" paragraph>
                4. <strong>仓位规划</strong>：事先规划好每一级的卖出比例，不要因贪婪或恐惧而偏离计划。
              </Typography>
              <Typography variant="body2" paragraph>
                5. <strong>趋势确认</strong>：只有在上涨趋势明确且持续的情况下才执行后续级别的减仓操作，不要在震荡市中频繁操作。
              </Typography>
              <Typography variant="body2" paragraph>
                4. <strong>保留底仓</strong>：考虑保留少量底仓，以应对可能的继续上涨，避免踏空。
              </Typography>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>风险提示</strong>：任何投资策略都存在风险，金字塔买卖法不保证盈利。在实际操作中，请结合自身风险承受能力和市场情况灵活应用。本计算器仅提供参考，不构成投资建议。
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PyramidCalculator;